import { useMemo } from "react";

import { find, map, reduce, times, toLower } from "lodash/fp";
import { Info } from "luxon";

import { Stack, Typography } from "@mui/material";

import useMetricsQuery from "@app/analytics/hooks/metrics/useMetricsQuery";
import * as Filters from "@app/analytics/meetops/filters";
import { ParsedMetric } from "@app/analytics/meetops/metrics";
import { DateRange, MeetOpsQuery } from "@app/analytics/meetops/types";
import ErrorContent from "@app/analytics/WorkspaceOverview/components/ErrorContent";
import { TimeOfDayBin, DayOfWeekLong, DayOfWeekShort } from "@app/common/types";
import { rotate } from "@app/utils/arrays";

import DayAndTimeChart, { DayAndTimeChartProps } from "./DayAndTimeChart";
import { useWorkspaceOverview } from "../WorkspaceOverviewProvider";

interface ApiDayOfWeekData {
  dayOfWeek: DayOfWeekShort;
  timeOfDay: TimeOfDayBin;
  engagement: ParsedMetric;
}

const TEN_MINUTES = 10 * 60 * 1000;

const getDefaultChartDataBlock = () => {
  return times((i: number) => ({ block: i, avgScore: null }), 5);
};

const BIN_TO_BLOCK: { [k in TimeOfDayBin]: number } = {
  hour_bin_00_03: -1,
  hour_bin_03_06: -1,
  hour_bin_06_09: 0,
  hour_bin_09_12: 1,
  hour_bin_12_15: 2,
  hour_bin_15_18: 3,
  hour_bin_18_21: 4,
  hour_bin_21_24: -1,
};

const getDefaultChartData = () => {
  // Rotate so that sunday is first in the list
  const lowerDayOfWeekLong = rotate(
    -1,
    map(toLower, Info.weekdays())
  ) as DayOfWeekLong[];

  return reduce(
    (acc, dayOfWeek) => {
      acc[dayOfWeek] = getDefaultChartDataBlock();
      return acc;
    },
    {} as NonNullable<DayAndTimeChartProps["data"]>,
    lowerDayOfWeekLong
  );
};

const getQuery = (dateRange: DateRange): MeetOpsQuery => {
  return {
    dateRanges: [dateRange],
    dimensions: ["dayOfWeek", "timeOfDay"],
    metrics: ["engagement"],
    filters: [Filters.multiParticipantMeeting()],
    people: "everyone",
  };
};

const getDayOfWeekLongFromShort = (
  dayOfWeek: DayOfWeekShort
): DayOfWeekLong => {
  const shortIndex = map(toLower, Info.weekdays("short")).indexOf(dayOfWeek);
  return toLower(Info.weekdays()[shortIndex]) as DayOfWeekLong;
};

const mapMetricsToChartData = (
  metrics: ApiDayOfWeekData[] | null
): DayAndTimeChartProps["data"] => {
  const chartData = getDefaultChartData();

  if (!metrics) return chartData;

  metrics.forEach(({ dayOfWeek, timeOfDay, engagement }) => {
    const dayOfWeekLong = getDayOfWeekLongFromShort(dayOfWeek);
    const block = BIN_TO_BLOCK[timeOfDay];

    const dataBlock = find({ block }, chartData[dayOfWeekLong]);
    if (dataBlock) {
      dataBlock.avgScore = engagement.value;
    }
  });

  return chartData;
};

const DayAndTimeTabModule = () => {
  const { queryDateRanges } = useWorkspaceOverview();
  const dayAndTimeQuery = useMetricsQuery(getQuery(queryDateRanges[0]), {
    staleTime: TEN_MINUTES,
  });

  const { data, isInitialLoading, isError, refetch } = dayAndTimeQuery;

  const queryData: ApiDayOfWeekData[] | null = data?.results[0].data ?? null;
  const chartData = useMemo(
    () => mapMetricsToChartData(queryData),
    [queryData]
  );

  return (
    <Stack sx={{ width: "100%" }}>
      <Typography
        variant="bodySecondaryReg"
        sx={{ color: "shades.neutral.600", pb: 1 }}
      >
        Best days and times to meet based on aggregate Engagement
      </Typography>
      {isError ? (
        <ErrorContent onRetry={refetch} sx={{ height: "210px" }} />
      ) : (
        <DayAndTimeChart data={chartData} loading={isInitialLoading} />
      )}
    </Stack>
  );
};

export default DayAndTimeTabModule;
